import axios from 'axios'
import { useQuery, useQueryClient, useInfiniteQuery } from 'react-query'

import { baseUrl } from 'configs/api'
import { APP_LOGIN_STORAGE_KEY } from 'configs/auth'
import {
  IS_PROFILE_REVAMP_PHASE_TWO_ENABLED,
  IS_PROFILE_REVAMP_PHASE_THREE_ENABLED,
} from 'configs/toggle'
import { useQueryApi, useMutationApi, api, handleCommonErrors } from 'utils/api'
import { useAuth } from 'utils/auth'
import {
  InternalServerError,
  NetworkError,
} from 'app/ProfileRevampPage/utils/customError'

import type { AxiosError, AxiosResponse } from 'axios'
import type { Profile } from 'types/user'
import type {
  TResponseSuccessGetProfileById,
  TResponseSuccessGetProfileToolkitByType,
  TResponseSuccessGetProfileInfoById,
  TResponseSuccessGetProfilePersonalData,
  TResponseErrorGetProfileInfoById,
  TRequestPayloadUpdateProfileInfo,
  TResponseSuccessUpdateProfileImages,
  TRequestPayloadUpdateProfileImages,
} from 'types/profile'
import type { TResponseSuccessGetCertificates } from 'types/sertifikat'

interface ResponseSuccessProfile {
  data: Profile
}

export const BASE_PATH = '/teachers/v1alpha2/users/profiles'

function handleError(status, path, error) {
  if (error?.message === 'Network Error') {
    throw new NetworkError(path, error)
  } else if ([400, 403].includes(status) || status >= 500) {
    throw new InternalServerError(path, error)
  } else {
    handleCommonErrors(status, path, error)
  }
}

export function useAPIProfile() {
  return useQueryApi<ResponseSuccessProfile>('/teachers/v1alpha1/profile', {
    queryConfig: {
      useErrorBoundary: false,
    },
  })
}

export function useAPIGetProfileById(profileId: string) {
  const apiPath = `${BASE_PATH}/${profileId}`
  const { session } = useAuth()

  return useQuery<TResponseSuccessGetProfileById>(
    apiPath,
    async () => {
      let response: TResponseSuccessGetProfileById
      if (Boolean(session)) {
        response = await api<TResponseSuccessGetProfileById>(
          apiPath,
          {},
          APP_LOGIN_STORAGE_KEY,
          false,
          undefined,
          (param) => param,
          (status, path, error) => {
            handleError(status ?? 500, path, error)
          }
        )
      } else {
        try {
          response = (
            await axios.get<any, AxiosResponse<TResponseSuccessGetProfileById>>(
              `${baseUrl}${apiPath}`
            )
          ).data
        } catch (error) {
          handleError(error?.response?.status, apiPath, error.response)
        }
      }
      return response
    },
    {
      enabled: true,
      useErrorBoundary: true,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}

export function useAPIGetProfileInfoById(profileId: string) {
  const apiPath = `${BASE_PATH}/${profileId}/info`
  const { session } = useAuth()

  return useQuery<TResponseSuccessGetProfileInfoById>(
    apiPath,
    async () => {
      let response: TResponseSuccessGetProfileInfoById
      if (Boolean(session)) {
        response = await api<TResponseSuccessGetProfileInfoById>(apiPath)
      } else {
        try {
          response = (
            await axios.get<
              any,
              AxiosResponse<TResponseSuccessGetProfileInfoById>
            >(`${baseUrl}${apiPath}`)
          ).data
        } catch (error) {
          handleCommonErrors(error?.response?.status, apiPath, error.response)
        }
      }
      return response
    },
    {
      enabled: IS_PROFILE_REVAMP_PHASE_TWO_ENABLED,
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}

export function useAPIGetProfilePersonalData() {
  const apiPath = `${BASE_PATH}/me`
  const { session } = useAuth()
  const profileId = session?.user?.id ?? ''

  return useQuery<TResponseSuccessGetProfilePersonalData>(
    [apiPath, profileId],
    () => api<TResponseSuccessGetProfilePersonalData>(apiPath),
    {
      enabled: IS_PROFILE_REVAMP_PHASE_THREE_ENABLED,
      useErrorBoundary: false,
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}

type TParamsGetProfileToolkitByType = {
  offset?: number
  limit: number
}
export function useAPIGetProfileToolkitById(
  userId: string,
  category: string,
  params: TParamsGetProfileToolkitByType = { offset: 0, limit: 25 }
) {
  const apiPath = `${BASE_PATH}/${userId}/toolkits/${category}`
  const { session } = useAuth()

  return useInfiniteQuery<TResponseSuccessGetProfileToolkitByType, Error>(
    [apiPath, params],
    async ({ pageParam }) => {
      let response

      if (Boolean(session)) {
        response = await api<TResponseSuccessGetProfileToolkitByType>(apiPath, {
          params: pageParam || params,
        })
      } else {
        try {
          response = (
            await axios.get<
              any,
              AxiosResponse<TResponseSuccessGetProfileToolkitByType>
            >(`${baseUrl}${apiPath}`, {
              params: pageParam || params,
            })
          ).data
        } catch (error) {
          handleCommonErrors(error?.response?.status, apiPath, error.response)
        }
      }
      return response
    },
    {
      enabled: Boolean(userId),
      getNextPageParam({ meta }) {
        if (meta.total <= meta.offset + params.limit) return

        return {
          offset: meta.offset + params.limit,
          limit: params.limit,
        }
      },
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}

export function useMutateAPIProfileInfoUpdate() {
  const queryClient = useQueryClient()
  const { session } = useAuth()
  const profileId = session?.user?.id as string

  return useMutationApi<
    TResponseSuccessGetProfileInfoById,
    TResponseErrorGetProfileInfoById,
    TRequestPayloadUpdateProfileInfo
  >(`${BASE_PATH}/info`, {
    axiosConfig: { method: 'PUT' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries(`${BASE_PATH}/${profileId}/info`)
      },
    },
  })
}

export function useMutateAPIProfileImageUpdate() {
  const queryClient = useQueryClient()
  const { session } = useAuth()
  const profileId = session?.user?.id as string

  return useMutationApi<
    TResponseSuccessUpdateProfileImages,
    AxiosError,
    TRequestPayloadUpdateProfileImages
  >(`${BASE_PATH}/images`, {
    axiosConfig: { method: 'PUT' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries(`${BASE_PATH}/${profileId}`)
      },
    },
  })
}

export function useAPIGetProfileCertificatesById(
  userId: string,
  params: {
    offset?: number
    limit: number
  } = { offset: 0, limit: 10 }
) {
  const apiPath = `${BASE_PATH}/${userId}/certificates`

  return useInfiniteQuery<TResponseSuccessGetCertificates, Error>(
    ['profile-certificates', userId, params],
    async ({ pageParam }) => {
      try {
        const response = await api<TResponseSuccessGetCertificates>(
          apiPath,
          {
            params: {
              limit: pageParam?.limit || params?.limit,
              offset: pageParam?.offset || params?.offset,
            },
          },
          APP_LOGIN_STORAGE_KEY,
          false,
          undefined,
          (param) => param,
          (status, path, error) => {
            handleError(status ?? 500, path, error)
          }
        )
        return response
      } catch (error) {
        if (pageParam?.offset >= 10) {
          const data = {
            data: [],
            meta: {
              ...(pageParam || params),
              total: pageParam?.total ?? 0,
              offset: pageParam?.offset ?? 0,
              isNextPageError: true,
            },
          }
          return data
        }

        throw error
      }
    },
    {
      enabled: Boolean(userId),
      useErrorBoundary: true,
      getNextPageParam({ meta }) {
        if (meta.total <= (meta?.offset ?? 0) + params.limit) return

        return {
          offset: meta.offset + params.limit,
          limit: params.limit,
          total: meta.total,
        }
      },
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    }
  )
}
